import OctImage from '@/components/image/OctImage.js'

/** Server that provides apis */
//const server = `${/localhost/.test(location.hostname) ? 'https://d2nu9qylym8w25.cloudfront.net' : '' }/api/v1`
const server = `${/localhost/.test(location.hostname) ? 'https://stg.marph.com' : '' }/api/v1`

const state = {
  api: {
    me: `${server}/me`,
    users: `${server}/users`,
    artworks: `${server}/artworks`,
    exhibitions: `${server}/exhibitions`,
    news: `${server}/news`,
    artfairs: `/assets/global/data/artfairs.json`,
    platforms: `/assets/global/data/platforms.json`,
    upload: `${server}/photos`,
    mail: `${server}/mail`,
    email: `${server}/auth0`,
    favorite: `${server}/favorites`,
    relations: userId => `${server}/users/${userId}/relations`,
  }
}

/** Padder */
const pad = (num, d) => (Math.pow(10, d) + Number(num)).toString().substr(1, d)

/**
 * String type check
 */
const isString = (val) => typeof (val) === 'string' || val instanceof String

/** Convert time into date */
const timeToDateString = (val) => {
  const date = new Date(val*1000)
  return !isNaN(date.getFullYear()) ?
          `${pad(date.getFullYear(), 4)}-${pad((date.getMonth()+1), 2)}-${pad(date.getDate(), 2)}` :
          ''
}

/** Convert string into time */
const stringToTime = (val) => {
  const date = new Date(val.replace(/-/g, '/'))
  return !isNaN(date.getFullYear()) ? date.getTime()/1000 : 0
}

/**
 * Normalize image object
 */
const normalizeImage = (item) => {
  if (item.lowResolution === undefined) // Item is invalid
    return OctImage()

  // Item is valid
  return item
}

/**
 * Normalize user object
 */
const normalizeUser = (item) => {
  // Clean up API bugs
  if (item.profileIcon !== undefined)
    item.profileIcon = normalizeImage(item.profileIcon)

  if (item.coverImage !== undefined)
    item.coverImage = normalizeImage(item.coverImage)

  item.data = item.data && !Array.isArray(item.data) && typeof item.data === 'object' ?
    item.data : {}

  if (item.tags !== undefined)
    item.tags = Array.isArray(item.tags) ? item.tags : []

  if (item.holidays !== undefined)
    item.holidays = Array.isArray(item.holidays) ? item.holidays : []

  // Add extra propeties
  item.data.businessNote = item.data.businessNote || ''
  item.data.businessNoteEN = item.data.businessNoteEN || ''
  item.data.history = item.data.history || ''
  item.data.historyEN = item.data.historyEN || ''
  // Related accounts
  item.data.relatedAccounts = item.data.relatedAccounts || []

  try {
    if (item.position !== undefined)
      item.position = Array.isArray(item.position) ?
        item.position : isString(item.position) ?
          JSON.parse(item.position) : []
  } catch {
    item.position = [item.position]
  }

  // Add user status
  if (!item.data.status)
    item.data.status = 'private'

  // News subscription
  item.subscription = !item.unsubscribe

  return item
}

/**
 * Normalize news object
 */
const normalizeNews = (item) => {
  // Clean up API bugs
  item.publishedAt = Number(item.publishedAt)*1000

  item.addresses = !item.addresses ? [] : (!Array.isArray(item.addresses) ? item.addresses.split(',') : item.addresses)
  item.exhibition = Array.isArray(item.exhibition) ? {} : item.exhibition

  // Initialize data properties
  item.data = item.data && !Array.isArray(item.data) && typeof item.data === 'object' ?
    item.data : {
    }

  // Initialize sentences
  item.data.sentences = item.data.sentences || {
      title: null,
      headline: null,
      description: null
    }

  // Normalize data field
  item.data = Object.assign({
    isArtFair: false
  }, item.data)

  return item
}

/**
 * Normalize artwork object
 */
const normalizeArtwork = (item) => {
  // Clean up api bug
  item.price = Number(item.price)
  item.publishedAt = item.publishedAt.toString()
  // Nomalize data object
  item.data = item.data || { url: '' }

  // Add extra galleries property
  item.data = Object.assign({extraGalleries: []}, item.data)

  // Add size property
  if (!item.data.sizes)
    item.data.sizes = [
      {
        width: '',
        height: '',
        depth: ''
      },
      {
        width: '',
        height: '',
        depth: ''
      }
    ]

  item.artists.forEach(item => item = normalizeUser(item))
  item.materials = Array.isArray(item.materials) ? item.materials : isString(item.materials) ? JSON.parse(item.materials) : []

  item.tags = Array.isArray(item.tags) ? item.tags : []

  return item
}

/**
 * Normalize exhibition object
 */
const normalizeExhibition = (item) => {
  // Clean up api bug
  if (!isNaN(item.startDate) && item.startDate !== 0)
    item.startDate = timeToDateString(item.startDate)
  else if (item.startDate === 0)
    item.startDate = ''

  if (!isNaN(item.endDate) && item.endDate !== 0)
    item.endDate = timeToDateString(item.endDate)
  else if (item.endDate === 0)
    item.endDate = ''

  item.data = item.data && !Array.isArray(item.data) && typeof item.data === 'object' ? item.data : {}
  
  // Add 'publicArtwoks' property to the data object
  item.data.publicArtworks = item.data.publicArtworks || []

  // Nomalize all artists
  item.artists = item.artists.map(item => normalizeUser(item))

  // Nomalize all organizers
  item.organizers = item.organizers.map(item => normalizeUser(item))

  // Nomalize all venues
  item.venues = item.venues.map(item => normalizeUser(item))

  return item
}

/** 
 * Check the request is succeeded
 */
const getParsedResponse = (response) => {
  if (response.code)
    throw response

  return response
}

export default {
  /**
   * Get users
   */
  async getUsers (queries={}, token=null) {
    const query = new URLSearchParams(queries)

    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.users}?${query}`, options)
    const response = getParsedResponse(await json.json())

    response.items = response.items.map(item => normalizeUser(item))
    
    return response
  },
  /**
   * Get user
   */
  async getUser (displayName=null, token=null) {
    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    //displayName = 'marph006'

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(displayName ? `${state.api.users}/${displayName}` : state.api.me, options)

    return normalizeUser(getParsedResponse(await json.json()))
  },
  /**
   * Update user
   */
  async updateUser (payload, token) {
    const isNew = !payload.id || payload.id === ''
    // Set end point
    const endPoint = isNew ? state.api.users : `${state.api.users}/${payload.id}`
    // Copy payload
    const data = JSON.parse(JSON.stringify(payload))

    // Delete id property
    isNew && delete data.id

    // News subscription
    data.unsubscribe = !data.subscription
    delete data.subscription

    /*
    delete data.email
    delete data.name
    data.type = 'gallery'
    data.artfair = true
    */

    //data.tags = encodeURIComponent(JSON.stringify(data.tags))

    // Set options
    const options = {
      method:  isNew ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    }

    const json = await fetch(endPoint, options)

    return getParsedResponse(await json.json())
  },
  /**
   * Update email
   */
  async updateEmail (email, token) {
    // Set options
    const options = {
      method:  'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({address: email})
    }

    const json = await fetch(state.api.email, options)

    return await json.json()
  },
  /**
   * Delete a user
   */
  async removeUser (id, token) {
    // Set options
    const options = {
      method:  'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      }
    }

    const json = await fetch(`${state.api.users}/${id}`, options)

    return getParsedResponse(await json.json())
  },
  /**
   * Get related accounts
   */
  async getRelatedAccounts (userId, token) {
    // Set options
    const options = {
      method:  'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      }
    }

    const json = await fetch(state.api.relations(userId), options)

    return await json.json()
  },
  /**
   * Update related accounts
   */
  async updateRelatedAccounts (userId, relatedAccounts, token) {
    // Set options
    const options = {
      method:  'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(relatedAccounts)
    }

    const json = await fetch(state.api.relations(userId), options)

    return await json.json()
  },
  /**
   * Get artworks
   */
  async getArtworks (queries={}, token=null) {
    const query = new URLSearchParams(queries)

    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.artworks}?${query}`, options)
    const response = getParsedResponse(await json.json())

    response.items = response.items.map(item => normalizeArtwork(item))
    
    return response
  },
  /**
   * Get artwork
   */
  async getArtwork (id, token=null) {
    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.artworks}/${id}`, options)
    const artwork = normalizeArtwork(getParsedResponse(await json.json()))

    // Retrieve exhibition data
    const queries = {artwork: artwork.id}
    artwork.exhibitions = (await this.getExhibitions(queries, token)).items

    return artwork
  },
  /**
   * Remove artwork
   */
  async removeArtwork (id, token=null) {
    // Set options
    const options = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.artworks}/${id}`, options)

    return getParsedResponse(await json.json())
  },
  /**
   * Update artwork
   */
  async updateArtwork (payload, token) {
    const isNew = !payload.id || payload.id === ''
    // Set end point
    const endPoint = isNew ? state.api.artworks : `${state.api.artworks}/${payload.id}`
    // Copy payload
    const data = JSON.parse(JSON.stringify(payload))

    // Normalize materials
    data.materials = data.materials.filter(item => item)

    // Delete id property
    isNew && delete data.id

    //data.tags = encodeURIComponent(JSON.stringify(data.tags))

    // Set options
    const options = {
      method:  isNew ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    }

    const json = await fetch(endPoint, options)
    return getParsedResponse(await json.json())
  },
  /**
   * Get all exhibitions
   */
  async getAllExhibitions (queries={}, token=null) {
    // Set page to 1
    queries.page = 1
    // All items
    const all = {total: 0, items: []}
    // Loop guard
    let hasNext = true

    while (hasNext) {
      // Fetch current result
      const response = await this.getExhibitions(queries, token)
      // Add fetched exhibitions
      all.items = all.items.concat(response.items)
      // Increment page
      queries.page++
      // Update guard
      hasNext = response.items.length && all.items.length < response.total
    }

    all.items = all.items.map(item => normalizeExhibition(item))
    
    return all 
  },
  /**
   * Get exhibitions
   */
  async getExhibitions (queries={}, token=null) {
    const query = new URLSearchParams(queries)

    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.exhibitions}?${query}`, options)
    const response = getParsedResponse(await json.json())

    response.items = response.items.map(item => normalizeExhibition(item))
    
    return response
  },
  /**
   * Get exhibitions
   */
  async getExhibition (id, token=null, force=true) {
    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.exhibitions}/${id}`, options)
    const exhibition = normalizeExhibition(getParsedResponse(await json.json()))

    // Update venues
    if (force && exhibition.venues.length) {
      // Ids to retrieve gallery data
      const ids = exhibition.venues.filter(item => item.id !== undefined)
      // Extract id from Object list
      const queries = {ids: ids.map(item => item.id)}
      // Fetch all galleries
      const data = await this.getUsers(queries, token)
      const galleries = {items: []}

      // Fetch all gallery details
      for await (let item of data.items) {
        const user = await this.getUser(item.displayName, token)
        galleries.items.push(user)
      }

      // Reconstruct venues
      exhibition.venues = exhibition.venues.reduce((acc, cur)=> {
        // Push current venue if venue is not gallery
        if (cur.id === undefined) {
          acc.push(cur)
          return acc
        }

        // Check the venue is a gallery and push it
        const filtered = galleries.items.filter(item => cur.id === item.id)
        filtered && acc.push(normalizeUser(filtered[0]))

        return acc
      }, [])
    }

    // Get public artworks
    if (force && exhibition.data.publicArtworks.length) {
      // Artworks
      const artworks = (await this.getArtworks({
        ids: exhibition.data.publicArtworks.join(','),
        counts: 100
      }, token)).items
      // Public artworks
      const p = exhibition.data.publicArtworks

      // Sort artworks
      exhibition.data.publicArtworks = artworks.sort((a, b) => p.indexOf(a.id) - p.indexOf(b.id))
    }

    /** Get artworks */
    if (force)
      exhibition.artworks  = (await this.getArtworks({
        exhibition: exhibition.id,
        counts: 100
      }, token)).items

    return exhibition
  },
  /**
   * Update exhibition
   */
  async updateExhibition (payload, token) {
    const isNew = !payload.id || payload.id === ''
    // Set end point
    const endPoint = isNew ? state.api.exhibitions : `${state.api.exhibitions}/${payload.id}`
    // Copy payload
    const data = JSON.parse(JSON.stringify(payload))

    // Normalize 'publicArtworks' property. Only id
    data.data.publicArtworks = data.data.publicArtworks.map(item => item.id)

    // Convert date into time
    if (data.startDate)
      data.startDate = stringToTime(data.startDate)

    // Convert date into time
    if (data.endDate)
      data.endDate = stringToTime(data.endDate)

    // Delete id property
    isNew && delete data.id

    //data.tags = encodeURIComponent(JSON.stringify(data.tags))

    // Set options
    const options = {
      method:  isNew ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    }

    const json = await fetch(endPoint, options)
    return getParsedResponse(await json.json())
  },
  /**
   * Remove exhibition
   */
  async removeExhibition (id, token=null) {
    // Set options
    const options = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.exhibitions}/${id}`, options)

    return getParsedResponse(await json.json())
  },
  /**
   * Get newss
   */
  async getNews (queries={}, token=null) {
    const query = new URLSearchParams(queries)

    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.news}?${query}`, options)
    const response = getParsedResponse(await json.json())

    response.items = response.items.map(item => normalizeNews(item))
    
    return response
  },
  /**
   * Get exhibitions
   */
  async getANews (id, token=null) {
    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.news}/${id}`, options)
    const news = normalizeNews(getParsedResponse(await json.json()))

    return news
  },
  /**
   * Update news
   */
  async updateNews (payload, token) {
    const isNew = !payload.id || payload.id === ''
    // Set end point
    const endPoint = isNew ? state.api.news : `${state.api.news}/${payload.id}`
    // Copy payload
    const data = JSON.parse(JSON.stringify(payload))

    data.publishedAt = data.publishedAt ? data.publishedAt/1000 : 0

    // Remove exhibition if not sat
    if (!data.exhibition.id)
      delete data.exhibition

    // Delete id property
    isNew && delete data.id

    //data.tags = encodeURIComponent(JSON.stringify(data.tags))

    // Set options
    const options = {
      method:  isNew ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    }

    const json = await fetch(endPoint, options)
    return getParsedResponse(await json.json())
  },
  /**
   * Remove news
   */
  async removeNews (id, token=null) {
    // Set options
    const options = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.news}/${id}`, options)

    return getParsedResponse(await json.json())
  },
  /**
   * Get artfairs
   */
  async getArtFairs () {
    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    const json = await fetch(`${state.api.artfairs}`, options)
    const response = {
      items: getParsedResponse(await json.json())
    }

    // Set total items
    response.total = response.items.length

    return response
  },
  /**
   * Get platforms
   */
  async getPlatforms () {
    // Set options
    const options = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }

    const json = await fetch(`${state.api.platforms}`, options)
    const response = {
      items: getParsedResponse(await json.json())
    }

    // Set total items
    response.total = response.items.length

    return response
  },
  /**
   * Upload a file
   */
  async upload (file, token) {
    // Create form data to send
    const formData = new FormData()
    formData.append('file', file)

    let query = ''

    // Add mime type and extention except images
    if (!/^image\//.test(file.type)) {
      // Extention
      const extention = file.name.split('.').pop()
      // Build query
      query = `?mime=${file.type}&ext=${extention || 'data'}`
    }

    // Set options
    const options = {
      method: 'POST',
      headers: {
        'X-Authorization': `Bearer ${token}`
      },
      body: formData
    }

    // Send to api
    const response = getParsedResponse(await (await fetch(state.api.upload + query, options)).json())

    // Add alt property
    response.alt = response.alt || ''

    return response
  },
  /**
   * Send mail
   */
  async sendMail ({ids, title, body}, token) {
    // Copy payload
    const data = {
      ids: ids,
      title: title,
      body: body
    }

    // Set options
    const options = {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    }

    const json = await fetch(state.api.mail, options)
    return getParsedResponse(await json.json())
  },
  /**
   * Add favorite item
   */
  async addFavorite (id, type, token=null) {
    // Set options
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: id,
        type: type
      })
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.favorite}/`, options)
    const response = getParsedResponse(await json.json())

    return response
  },
  /**
   * Remove favorite item
   */
  async removeFavorite (id, token=null) {
    // Set options
    const options = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
    }

    if (token) // Set token
      options.headers['X-Authorization'] = `Bearer ${token}`

    const json = await fetch(`${state.api.favorite}/${id}`, options)
    const response = getParsedResponse(await json.json())

    return response
  }
}
